/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import ModelPropertiesSelector from "../ModelPropertiesSelector/ModelPropertiesSelector";
import rotate_icon from "../../../images/rotate-icon.png";
import UserScoreOverview from "../UsersScores/UsersScores";
import { User } from "../../../types/user";
import "./interactivedrawingmodel.style.scss";
import { CroqeeInteractiveModel } from "@croqee/interactive-model";
import { Box } from "@mui/material";
import ModelSelectorToggle from "../ModelSelectorToggle/ModelSelectorToggle";
import ModelSelector from "../../modelselector/ModelSelector";
import TimerOptionsToggle from "../TimerOptionsToggle/TimerOptionsToggle";
import TimerOptions from "../../timeroptions/TimerOptions";
import { setActiveModelPropertiesAndIdentity } from "../../../state/actions";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { colors } from "../../../constants/site-content";

const INTERACTIVE_DRAWING_MODEL_CLASS = "interactive-drawing-model";

// Initialize Draco loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath(
  "https://www.gstatic.com/draco/versioned/decoders/1.5.6/"
); // Use CDN path
dracoLoader.preload();

// Initialize GLTF loader with Draco support
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

export type ThreeDimPosition = {
  x: number;
  y: number;
  z: number;
};
export function formatXYZ(obj) {
  return `x${obj.x}&y${obj.y}&z${obj.z}`;
}
export type OutLineSpec = {
  normalMultiplier?: number;
  outlineSharpness?: number;
  normalBias?: number;
  depthMultiplier?: number;
  overLineNormalBias?: number;
  overLineNormalMultiplier?: number;
};
export type InteractiveModelProperties = {
  modelName?: string;
  overLineColor?: string;
  modelUrl: string;
  hasTexture?: boolean;
  cameraPosition: {
    x: number;
    y: number;
    z: number;
  };
  modelScale: number;
  controlTarget: {
    x: number;
    y: number;
    z: number;
  };
  outLineSpec: {
    normalMultiplier: number;
    outlineSharpness: number;
    normalBias: number;
    depthMultiplier: number;
    overLineNormalMultiplier?: number;
    overLineNormalBias?: number;
  };
};
type Props = {
  componentRef: any;
  modelUrl: string;
  outLineColor?: number;
  cameraPosition?: ThreeDimPosition;
  hasTexture?: boolean;
  allowUserCameraControl?: boolean;
  compete?: boolean;
  showUserScores?: boolean;
  user?: User;
  playingUsers?: any[];
  modelScale?: number;
  controlTarget?: ThreeDimPosition;
  normalMultiplier?: number;
  outLineSpec?: OutLineSpec;
  leftHand?: boolean;
  setShouldResetCanvas?: (bool: boolean) => void;
  modelName?: string;
};
type MappedProps = {
  canvasHeight: number;
  canvasWidth: number;
  modelViewWidth?: number;
  modelViewHeight?: number;
  allowUserCameraControl: boolean;
  isDrawingStarted: boolean;
  setActiveModelPropertiesAndIdentity: (payload: any) => void;
};

const InteractiveDrawingModel = (props: Props & MappedProps): JSX.Element => {
  const [displayModelChangeOverlay, setDisplayModelChangeOverlay] =
    useState(false);
  const [displayTimerOptionsOverlay, setDisplayTimerOptionsOverlay] =
    useState(false);

  const [displayOutline, setDisplayOutline] = useState(true);
  const [displayModel, setDisplayModel] = useState(true);
  const [displayTexture, setDisplayTexture] = useState(true);
  const [displayRotateHintOverlay, setDisplayRotateHintOverlay] =
    useState(false);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [modelLoadError, setModelLoadError] = useState<string | null>(null);

  const [allowUserCameraControl, setAllowUserCameraControl] = useState(
    props.allowUserCameraControl
  );

  useEffect(() => {
    if (displayRotateHintOverlay && props.isDrawingStarted) {
      hideRotateHintOverlay();
    }
  }, [displayRotateHintOverlay, props.isDrawingStarted]);

  useEffect(() => {
    if (!props.compete) {
      setAllowUserCameraControl(!props.isDrawingStarted);
    }
  }, [props.isDrawingStarted, props.compete]);
  const toggleDisplayOutline = () => {
    setDisplayOutline(!displayOutline);
  };
  const toggleDisplayModel = () => {
    setDisplayModel(!displayModel);
  };
  const toggleDisplayTexture = () => {
    setDisplayTexture((prev) => !prev);
  };
  const hideRotateHintOverlay = () => {
    setDisplayRotateHintOverlay(false);
  };

  const [interactiveModelProperties, setInteractiveModelProperties] =
    useState<InteractiveModelProperties>(null);
  useEffect(() => {
    setDisplayModel(true);
    setDisplayOutline(true);
  }, [
    interactiveModelProperties?.modelUrl,
    props?.modelUrl,
    props.cameraPosition,
  ]);
  const [cameraPositionChange, setCameraPositionChange] = useState(
    interactiveModelProperties?.cameraPosition ||
      props.cameraPosition || { x: 0, y: 20, z: 75 }
  );
  useEffect(() => {
    if (props.allowUserCameraControl) {
      setDisplayRotateHintOverlay(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowUserCameraControl,
    props?.modelUrl,
    interactiveModelProperties?.modelUrl,
  ]);
  useEffect(() => {
    const modelName = interactiveModelProperties?.modelName || props.modelName;
    const modelCameraPosition = formatXYZ(cameraPositionChange);
    props.setActiveModelPropertiesAndIdentity(
      `${modelName}_${modelCameraPosition}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    interactiveModelProperties?.modelUrl,
    props?.modelUrl,
    cameraPositionChange,
    interactiveModelProperties?.modelName,
    props?.modelName,
    props?.setActiveModelPropertiesAndIdentity,
  ]);

  // Function to load model with Draco compression
  const loadModel = async (url: string) => {
    try {
      setIsModelLoading(true);
      setModelLoadError(null);

      // Load the model with progress tracking
      const onProgress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setLoadingProgress(Math.round(progress));
        }
      };

      // Return a promise that resolves with the loaded model
      return new Promise((resolve, reject) => {
        gltfLoader.load(
          url,
          (gltf) => {
            setIsModelLoading(false);
            resolve(gltf);
          },
          onProgress,
          (error) => {
            console.error("Error loading model:", error);
            setModelLoadError(error.message);
            setIsModelLoading(false);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error("Error in loadModel:", error);
      setModelLoadError(error.message);
      setIsModelLoading(false);
      throw error;
    }
  };

  // Effect to handle model loading
  useEffect(() => {
    const modelUrl = interactiveModelProperties?.modelUrl || props.modelUrl;
    if (modelUrl && props.canvasWidth && props.canvasHeight) {
      loadModel(modelUrl).catch(console.error);
    }
  }, [
    props.modelUrl,
    interactiveModelProperties?.modelUrl,
    props.canvasWidth,
    props.canvasHeight,
  ]);

  const {
    canvasHeight,
    canvasWidth,
    componentRef,
    modelUrl,
    outLineColor,
    cameraPosition,
    compete,
    showUserScores,
    user,
    playingUsers,
    modelScale,
    controlTarget,
    outLineSpec,
    leftHand,
  } = props;
  return (
    <>
      {displayModelChangeOverlay && (
        <ModelSelector
          closeOverlay={() =>
            setDisplayModelChangeOverlay(!displayModelChangeOverlay)
          }
          setInteractiveModelProperties={setInteractiveModelProperties}
          setShouldResetCanvas={props.setShouldResetCanvas}
        />
      )}
      {displayTimerOptionsOverlay && (
        <TimerOptions
          closeOverlay={() =>
            setDisplayTimerOptionsOverlay(!displayTimerOptionsOverlay)
          }
        />
      )}
      <div
        className={
          allowUserCameraControl
            ? `${INTERACTIVE_DRAWING_MODEL_CLASS} ${INTERACTIVE_DRAWING_MODEL_CLASS}--enable-control`
            : INTERACTIVE_DRAWING_MODEL_CLASS
        }
        style={{
          width: `${props.modelViewWidth || canvasWidth}px`,
          height: `${props.modelViewHeight || canvasHeight}px`,
          position: "relative",
        }}
        onClick={() => hideRotateHintOverlay()}
        onTouchStart={() => hideRotateHintOverlay()}
      >
        {compete && showUserScores && (
          <UserScoreOverview
            width={canvasWidth}
            height={canvasHeight}
            playingUsers={playingUsers}
            user={user}
          />
        )}

        {displayRotateHintOverlay && (
          <div
            className={`${INTERACTIVE_DRAWING_MODEL_CLASS}__rotate-hint-overlay`}
          >
            <img src={rotate_icon} />
          </div>
        )}
        <Box></Box>
        <Box
          id="model-properties-selector-wrapper"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "auto",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            justifyContent: "center",
            left: leftHand ? "auto" : "8px",
            right: leftHand ? "8px" : "auto",
            gap: "8px",
            zIndex: 1,
          }}
        >
          <ModelPropertiesSelector
            height={canvasHeight}
            displayOutline={displayOutline}
            displayModel={displayModel}
            toggleDisplayOutline={toggleDisplayOutline}
            toggleDisplayModel={toggleDisplayModel}
          />
          {(interactiveModelProperties?.hasTexture ?? props.hasTexture) && (
            <div className="model-properties-selector">
              <span
                className={`model-properties-selector__texture ${
                  displayTexture
                    ? ""
                    : "model-properties-selector__texture--inactive"
                }`}
                onClick={toggleDisplayTexture}
              />
            </div>
          )}
          {!compete && (
            <ModelSelectorToggle
              height={canvasHeight}
              toggleOverlay={() =>
                setDisplayModelChangeOverlay(!displayModelChangeOverlay)
              }
            />
          )}
          {!compete && !props.isDrawingStarted && (
            <TimerOptionsToggle
              height={canvasHeight}
              toggleOverlay={() =>
                setDisplayTimerOptionsOverlay(!displayTimerOptionsOverlay)
              }
            />
          )}
        </Box>

        {isModelLoading && (
          <div
            className={`${INTERACTIVE_DRAWING_MODEL_CLASS}__loading-overlay`}
          >
            <div className="loading-spinner" />
            <div className="loading-text">
              Loading model... {loadingProgress}%
              {modelLoadError && (
                <div className="loading-error">Error: {modelLoadError}</div>
              )}
            </div>
          </div>
        )}

        {canvasWidth && canvasHeight && modelUrl && componentRef && (
          <CroqeeInteractiveModel
            overLineColor={
              interactiveModelProperties?.overLineColor
                ? parseInt(interactiveModelProperties?.overLineColor, 16)
                : undefined
            }
            onCameraPositionChange={(position) => {
              setCameraPositionChange(position);
            }}
            ref={componentRef}
            modelUrl={interactiveModelProperties?.modelUrl || modelUrl}
            displayTexture={displayTexture}
            outLineColor={outLineColor || 0x73a5c6}
            cameraPosition={
              interactiveModelProperties?.cameraPosition ||
              cameraPosition || { x: 0, y: 20, z: 75 }
            }
            displayOriginalModel={displayModel}
            displayModelOutline={displayOutline}
            modelWidth={props.modelViewWidth || canvasWidth}
            modelHeight={props.modelViewHeight || canvasHeight}
            allowUserCameraControl={allowUserCameraControl}
            modelScale={interactiveModelProperties?.modelScale || modelScale}
            controlTarget={
              interactiveModelProperties?.controlTarget || controlTarget
            }
            normalMultiplier={
              interactiveModelProperties?.outLineSpec?.normalMultiplier ||
              outLineSpec?.normalMultiplier
            }
            outlineSharpness={
              interactiveModelProperties?.outLineSpec?.outlineSharpness ||
              outLineSpec?.outlineSharpness
            }
            normalBias={
              interactiveModelProperties?.outLineSpec?.normalBias ||
              outLineSpec?.normalBias
            }
            depthMultiplier={
              interactiveModelProperties?.outLineSpec?.depthMultiplier ||
              outLineSpec?.depthMultiplier
            }
            overLineNormalBias={
              interactiveModelProperties
                ? interactiveModelProperties?.outLineSpec?.overLineNormalBias
                : outLineSpec?.overLineNormalBias
            }
            overLineNormalMultiplier={
              interactiveModelProperties
                ? interactiveModelProperties?.outLineSpec
                    ?.overLineNormalMultiplier
                : outLineSpec?.overLineNormalMultiplier
            }
          />
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    canvasHeight,
    canvasWidth,
    modelViewWidth,
    modelViewHeight,
    isDrawingStarted,
    user,
    leftHand,
  } = state;
  return {
    canvasHeight,
    canvasWidth,
    modelViewWidth,
    modelViewHeight,
    isDrawingStarted,
    user,
    leftHand,
  };
};
const mapDispatchToProps = (dispatch: any) => {
  return {
    setActiveModelPropertiesAndIdentity: (payload: any) =>
      dispatch(setActiveModelPropertiesAndIdentity(payload)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InteractiveDrawingModel);
